import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  Play,
  Youtube,
} from 'lucide-react';

import SEO from '../components/SEO';
import AdSlot from '../components/AdSlot';

const YOUTUBE_JSON_URL =
  'https://editoruma.github.io/portfolio/youtube.json';

interface VideoItem {
  id: string;
  title: string;
  videoId: string;
  description?: string;
  category?: string;
  duration?: string;
  publishedAt?: string;
}

interface YoutubeData {
  channel: {
    name: string;
    description: string;
    youtubeUrl?: string;
  };
  videos: VideoItem[];
}

const EnglishUmaWatch: React.FC = () => {
  const { videoId } = useParams<{ videoId: string }>();

  const [data, setData] = useState<YoutubeData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const footer = document.querySelector("footer");
    const header = document.querySelector("header");
    if (footer) footer.style.display = "none";
  if (header) header.style.display = "none";
    return () => {
      if (footer) footer.style.display = "";
      if (header) header.style.display = "";
    };
  }, []);

  useEffect(() => {
    const loadVideos = async () => {
      try {
        const response = await fetch(YOUTUBE_JSON_URL);

        if (!response.ok) {
          throw new Error('Unable to fetch video data');
        }

        const json: YoutubeData = await response.json();

        setData(json);
      } catch (error) {
        console.error('Video data error:', error);
      } finally {
        setLoading(false);
      }
    };

    loadVideos();
  }, []);

  const currentVideo = useMemo(() => {
    return data?.videos?.find(
      (video) => video.videoId === videoId
    );
  }, [data, videoId]);

  const relatedVideos = useMemo(() => {
    if (!data?.videos || !currentVideo) return [];

    const sameCategory = data.videos.filter(
      (video) =>
        video.videoId !== currentVideo.videoId &&
        video.category === currentVideo.category
    );

    const others = data.videos.filter(
      (video) =>
        video.videoId !== currentVideo.videoId &&
        video.category !== currentVideo.category
    );

    return [...sameCategory, ...others].slice(0, 4);
  }, [data, currentVideo]);

  const formatDate = (date?: string) => {
    if (!date) return '';

    const parsed = new Date(date);

    if (Number.isNaN(parsed.getTime())) {
      return date;
    }

    return parsed.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">

          <div className="w-12 h-12 border-4 border-sky-200 border-t-sky-600 rounded-full animate-spin mx-auto mb-4" />

          <p className="text-gray-600">
            Loading lesson...
          </p>

        </div>
      </div>
    );
  }

  if (!currentVideo) {
    return (
      <>
        <SEO
          title="Lesson Not Found"
          description="The requested English With Uma lesson could not be found."
          noIndex={true}
        />

        <div className="min-h-[70vh] flex items-center justify-center bg-gray-50 px-4">

          <div className="text-center max-w-lg">

            <div className="w-16 h-16 rounded-full bg-sky-100 flex items-center justify-center mx-auto mb-5">
              <Play className="w-7 h-7 text-sky-600" />
            </div>

            <h1 className="text-3xl font-bold text-gray-900">
              Lesson Not Found
            </h1>

            <p className="text-gray-600 mt-3">
              This lesson may have been removed or the URL may be incorrect.
            </p>

            <Link
              to="/english-with-uma"
              className="inline-flex items-center gap-2 mt-7 px-6 py-3 rounded-xl bg-sky-600 text-white font-semibold hover:bg-sky-700 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to English With Uma
            </Link>

          </div>

        </div>
      </>
    );
  }

  return (
    <>
      <SEO
        title={currentVideo.title}
        description={
          currentVideo.description ||
          `Watch ${currentVideo.title} from English With Uma and improve your English speaking, vocabulary and communication skills.`
        }
        keywords={[
          'English With Uma',
          currentVideo.title,
          currentVideo.category || '',
          'learn English',
          'English speaking',
          'spoken English',
          'English lessons',
        ]}
        image={`https://img.youtube.com/vi/${currentVideo.videoId}/maxresdefault.jpg`}
        schemaType="page"
      />

      <main className="bg-gray-50 min-h-screen pb-28">

        {/* ================= TOP ================= */}

        <section className="bg-white border-b border-gray-200">
          <div className="container mx-auto px-4 py-5">

            <div className="max-w-6xl mx-auto">

              <Link
                to="/english-with-uma"
                className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-sky-600 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to English With Uma
              </Link>

            </div>

          </div>
        </section>

        {/* ================= VIDEO ================= */}

        <section className="py-8 md:py-12">
          <div className="container mx-auto px-4">

            <div className="max-w-6xl mx-auto">

              <div className="bg-black rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl">

                <div className="aspect-video">

                  <iframe
                    className="w-full h-full"
                    src={`https://www.youtube.com/embed/${currentVideo.videoId}?rel=0`}
                    title={currentVideo.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />

                </div>

              </div>

              {/* ================= VIDEO INFO ================= */}

              <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8 mt-6 shadow-sm">

                <div className="flex flex-wrap items-center gap-3 mb-4">

                  {currentVideo.category && (
                    <span className="px-3 py-1 rounded-full bg-sky-50 text-sky-700 text-sm font-semibold">
                      {currentVideo.category}
                    </span>
                  )}

                  {currentVideo.duration && (
                    <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-600 text-sm">
                      {currentVideo.duration}
                    </span>
                  )}

                  {currentVideo.publishedAt && (
                    <span className="inline-flex items-center gap-1.5 text-sm text-gray-500">
                      <CalendarDays className="w-4 h-4" />
                      {formatDate(currentVideo.publishedAt)}
                    </span>
                  )}

                </div>

                <h1 className="text-2xl md:text-4xl font-bold text-gray-900 leading-tight">
                  {currentVideo.title}
                </h1>

                {currentVideo.description && (
                  <p className="text-gray-600 text-base md:text-lg leading-relaxed mt-5">
                    {currentVideo.description}
                  </p>
                )}

                {data?.channel.youtubeUrl && (
                  <a
                    href={data.channel.youtubeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 mt-6 px-5 py-3 rounded-xl bg-red-50 text-red-600 hover:bg-red-100 font-semibold transition-colors"
                  >
                    <Youtube className="w-5 h-5" />
                    Visit English With Uma on YouTube
                  </a>
                )}

              </div>

            </div>

          </div>
        </section>

        {/* ================= VIDEO AD ================= */}

        <section className="container mx-auto px-4">

          <div className="max-w-6xl mx-auto py-4">

            <AdSlot slot="inArticle1" />

          </div>

        </section>

        {/* ================= RELATED ================= */}

        {relatedVideos.length > 0 && (
          <section className="py-10 md:py-14">

            <div className="container mx-auto px-4">

              <div className="max-w-6xl mx-auto">

                <div className="flex items-end justify-between gap-4 mb-7">

                  <div>
                    <span className="text-sky-600 text-sm font-semibold uppercase tracking-wider">
                      Keep Learning
                    </span>

                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-1">
                      Related Lessons
                    </h2>
                  </div>

                  <Link
                    to="/english-with-uma"
                    className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-sky-600 hover:text-sky-700"
                  >
                    View All
                    <ArrowRight className="w-4 h-4" />
                  </Link>

                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">

                  {relatedVideos.map((video) => (
                    <Link
                      key={video.id}
                      to={`/english-with-uma/watch/${video.videoId}`}
                      className="group bg-white rounded-xl overflow-hidden border border-gray-200 hover:shadow-lg transition-all"
                    >

                      <div className="relative aspect-video overflow-hidden bg-gray-100">

                        <img
                          src={`https://img.youtube.com/vi/${video.videoId}/hqdefault.jpg`}
                          alt={video.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                        />

                        <div className="absolute inset-0 flex items-center justify-center">

                          <div className="w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <Play className="w-4 h-4 text-sky-600 fill-sky-600 ml-0.5" />
                          </div>

                        </div>

                      </div>

                      <div className="p-4">

                        {video.category && (
                          <span className="text-xs text-sky-600 font-semibold">
                            {video.category}
                          </span>
                        )}

                        <h3 className="font-bold text-gray-900 mt-1 line-clamp-2 group-hover:text-sky-600 transition-colors">
                          {video.title}
                        </h3>

                      </div>

                    </Link>
                  ))}

                </div>

              </div>

            </div>

          </section>
        )}

      </main>

      {/* ================= FIXED BOTTOM AD ================= */}

      <div className="fixed bottom-0 left-0 w-full z-50 bg-white border-t border-gray-200">

        <div className="max-w-7xl mx-auto h-[90px] flex items-center justify-center overflow-hidden px-2">

          <AdSlot slot="previewBottom" />

        </div>

      </div>
    </>
  );
};

export default EnglishUmaWatch;